import { useEffect, useState } from "react";
import Tree from "../../components/jsonSchemaEditor"
import JsonView from '@uiw/react-json-view';
import { JSONSchema7 } from "json-schema";
import { Button, ButtonGroup, Spinner, Tab, Tabs } from "@nextui-org/react";
import { ExtractorType, FileType } from "../../types/types";
import { FileUpload } from "../../components/fileUpload";
import { toCamelCase } from "../../utils/string";

import { httpsCallable } from 'firebase/functions';
import { useFirestore, useFunctions, useStorage } from "reactfire";
import { collection, doc, setDoc } from "firebase/firestore";
import { ref, getDownloadURL } from 'firebase/storage';
import HTMLView from "../../components/htmlView";
import { SavedTestRuns } from "./savedTestRuns";

export interface EditorProps {
    ext: ExtractorType,
    onSchemaChange?: (schema: JSONSchema7) => void;
    saveExtractor?: () => void;
}


export const Editor: React.FC<EditorProps> = ({ ext, onSchemaChange, saveExtractor }) => {

    const [isProcessing, setIsProcessing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [currentTab, setCurrentTab] = useState<string>("current");

    const functions = useFunctions();
    const firestore = useFirestore();
    const storage = useStorage();

    const emptySchema: JSONSchema7 = {
        title: toCamelCase(ext.name),
        description: '',
        type: 'object',
    }

    const [extractor, setExtractor] = useState<ExtractorType>(ext);
    const [schema, setSchema] = useState<JSONSchema7>(ext.schema || emptySchema);

    useEffect(() => {
        setExtractor(ext);
        setSchema(ext.schema || emptySchema);
    }, [ext]);

    const [response, setResponse] = useState<any>();
    const [error, setError] = useState<any>();

    const [downloadUrl, setDownloadUrl] = useState<string>("");

    const handleSchemaChange = (newSchema: JSONSchema7) => {
        setSchema(newSchema);
        if (onSchemaChange) {
            onSchemaChange(newSchema);
        }
    };

    const callTestRun = async () => {
        try {
            setIsProcessing(true);
            setError('');
            setResponse('');

            if (saveExtractor) saveExtractor();

            var fnName = 'api/testrun/' + extractor.id;

            const callableFunction = httpsCallable(functions, fnName);
            const result = await callableFunction(
                {
                    extractorId: extractor.id,
                }
            );
            console.log(result);
            setResponse(result.data);
        } catch (err) {
            setResponse('');
            setError(err);
            console.log(err);
        }
        setIsProcessing(false);
    };

    const saveTestRun = async () => {
        try {
            if (!response || !extractor.testFile) {
                setError({ message: "Please run a test first and ensure a test file is uploaded" });
                return;
            }

            setIsSaving(true);
            setError('');

            var fnName = 'api/savetestrun/' + extractor.id;

            const dataToSave = {
                schema: schema,
                testFile: extractor.testFile,
                output: response
            };
            console.log('Saving test run with data:', dataToSave);

            const callableFunction = httpsCallable(functions, fnName);
            const result = await callableFunction({
                data: dataToSave
            });
            console.log('Save result:', result);
        } catch (err) {
            setError(err);
            console.log(err);
        }
        setIsSaving(false);
    };

    useEffect(() => {
        if (extractor) {
            setResponse(extractor.output);
        }
    }, [extractor])

    useEffect(() => {
        async function fetchDownloadUrl() {
            const fileRef = ref(storage, extractor.output?.excel);
            try {
                const url = await getDownloadURL(fileRef);
                setDownloadUrl(url);
            } catch (error) {
                console.error("Error fetching download URL:", error);
                setDownloadUrl("");
            }
        }
        fetchDownloadUrl()
    }, [extractor.output, setDownloadUrl])

    const handleUpload = (newUploadFile: FileType) => {
        setDoc(doc(collection(firestore, 'extractors'), ext.id), {
            ...ext,
            testFile: newUploadFile
        });

        console.log('upload');
    }
    const handleDelete = () => {
        setDoc(doc(collection(firestore, 'extractors'), ext.id), {
            ...ext,
            testFile: null
        });
        console.log('delete');
    }

    if (!extractor) {
        return (
            <div className="flex w-full h-full justify-center items-center">
                <div className="text-2xl">Loading...</div>
            </div>
        )
    }

    const _showOutputs = () => {

        if (response) {
            return (
                <div className="h-full">
                    <Tabs aria-label="Options">
                        <Tab key="json" title="JSON">
                            {response.json ? <JsonView value={response.json} /> : <p>Failed to parse JSON</p>}
                        </Tab>
                        <Tab key="html" title="HTML">
                            {response.html ? <HTMLView html={response.html} /> : <p>Failed to parse HTML</p>}
                            {downloadUrl && downloadUrl != "" ?
                                <a href={downloadUrl}>
                                    <div className="rounded-md mt-[20px] p-[10px] bg-blue-500 fit-content w-fit text-white">
                                        Download Excel
                                    </div>
                                </a> : <p>Failed to create Excel table</p>}
                        </Tab>
                    </Tabs>
                </div >
            )
        }
    }

    return (
        <div className="flex w-full min-h-[65vh]">
            <div className="w-16 border-r pr-4 flex flex-col gap-2 items-center justify-start pt-8">
                <button
                    className={`p-3 rounded-lg transition-colors ${currentTab === "current" ? "bg-blue-100 text-blue-700 " : "hover:bg-gray-100"}`}
                    onClick={() => setCurrentTab("current")}
                    style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                >
                    Current
                </button>
                <button
                    className={`p-3 rounded-lg transition-colors ${currentTab === "saved" ? "bg-blue-100 text-blue-700 " : "hover:bg-gray-100"}`}
                    onClick={() => setCurrentTab("saved")}
                    style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                >
                    Saved
                </button>
            </div>
            <div className="flex-1">
                {currentTab === "current" && (
                    <div className="flex columns-2 gap-5 w-full">
                        <div className="flex-1 overflow-y-auto p-4 bg-base-100  ">
                            <div className="mt-2">
                                <div className="text-lg font-bold">Schema</div>
                                <span className="text-sm">The extractor will follow the format and instructions from the schema</span>
                            </div>
                            <Tree onSchemaChange={handleSchemaChange} schema={schema} />
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 bg-base-100 mt-2 ">
                            <div className="flex-1">
                                <div className="text-lg font-bold">Test Run</div>
                                <span className="text-sm">Test the response from the extractor by loading a sample file </span>
                            </div>
                            <div className="flex mt-5 justify-between bg-green-50 p-4  rounded-lg">
                                <FileUpload fileObj={extractor?.testFile} handleUpload={handleUpload} handleDelete={handleDelete} />
                                <ButtonGroup>
                                    <Button
                                        onClick={callTestRun}
                                        className="bg-green-500" aria-label="Test">
                                        Test
                                    </Button>
                                    {response && (
                                        <Button
                                            onClick={saveTestRun}
                                            isLoading={isSaving}
                                            className="bg-blue-500" aria-label="Save">
                                            Save
                                        </Button>
                                    )}
                                </ButtonGroup>
                            </div>
                            <div className="flex-col mt-5">
                                <div className="text-lg font-bold">Output</div>
                                <span className="text-sm">Response from the extractor for the given file </span>
                            </div>
                            <div className="flex flex-col mt-5">
                                {isProcessing && <div className="flex justify-center items-center h-full">
                                    <Spinner />
                                </div>}
                                {!isProcessing && error && <div className="text-red-500">{error.message}</div>}
                                {!isProcessing && _showOutputs()}
                            </div>

                        </div>
                    </div>
                )}
                {currentTab === "saved" && (
                    <SavedTestRuns extractorId={extractor.id} />
                )}
            </div>
        </div>
    )
}